import { Checkbox } from "components/ui/checkbox";
import { SkeletonCheckboxLoader } from "components/ui/SkeletonCheckboxLoader";
import { useChannels } from "core/hooks/gracenote";
import useDebounce from "core/hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import styles from "./LineupGroup.module.scss";
import { Channel, Lineup } from "types";
import { CircularProgress } from "@mui/material";

interface SearchInputProps {
    onSearchChange: (value: string) => void;
    readOnly?: boolean;
}

export const SearchInput: React.FC<SearchInputProps> = ({ onSearchChange, readOnly = false }) => {
    const [inputValue, setInputValue] = useState("");
    const debouncedValue = useDebounce(inputValue, 300);

    useEffect(() => {
        onSearchChange(debouncedValue);
    }, [debouncedValue, onSearchChange]);

    return (
        <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Search channels..."
            className={styles.searchInput}
            // Search should always be enabled, even in readOnly/view mode
        />
    );
};

interface LineupGroupProps {
    masterChannels: string[];
    providerChannel: any[] | null;
    lineup: Lineup;
    selectedChannels: string[];
    selectedTvPackage?: number | null;
    onChannelsChange: (channels: string[]) => void;
    onLoaded?: () => void;
    setChannelArray: React.Dispatch<React.SetStateAction<string[]>>;
    readOnly?: boolean;
}

export const LineupGroup: React.FC<LineupGroupProps> = ({
    providerChannel,
    lineup,
    selectedChannels,
    selectedTvPackage,
    onChannelsChange,
    onLoaded,
    setChannelArray,
    readOnly = false,
}) => {

    const { data: channels, isLoading, isError } = useChannels(lineup.lineupId);
    const [searchTerm, setSearchTerm] = useState("");

    const isDirectTvNonCustom = lineup.name?.startsWith("DIRECT") && selectedTvPackage !== null && selectedTvPackage !== 7;

    const getChannelNumber = (channelValue: string | number) => {
        const match = String(channelValue).match(/\d+/);
        if (!match) return null;
        const parsed = parseInt(match[0], 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const isLocalChannel = (channelNumber: string | number) => {
        const parsed = getChannelNumber(channelNumber);
        return parsed !== null && parsed <= 100;
    };

    const isChannelSelectable = (channelNumber: string | number) =>
        !readOnly && (!isDirectTvNonCustom || isLocalChannel(channelNumber));

    const handleChannelToggle = (channelId: string) => {
        // Don't allow toggling if disabled for this channel
        if (!isChannelSelectable(channelId)) return;
        
        let updatedSelectedChannels;
        if (selectedChannels.includes(channelId)) {
            updatedSelectedChannels = selectedChannels.filter((id) => id !== channelId);
        } else {
            updatedSelectedChannels = [...selectedChannels, channelId];
        }
        onChannelsChange(updatedSelectedChannels);
        setChannelArray(updatedSelectedChannels);
    };

    const selectedChannelsSet = useMemo(
        () => new Set(selectedChannels),
        [selectedChannels]
    );

    const filteredChannels = useMemo(() => {
        if (!searchTerm) return channels || [];
        return (
            channels?.filter(
                (channel: any) =>
                    (channel.callSign?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
                    channel.channel.toLowerCase().includes(searchTerm.toLowerCase())
            ) || []
        );
    }, [channels, searchTerm]);

    const selectableChannels = useMemo(
        () =>
            filteredChannels.filter(
                (channel: any) =>
                    !readOnly && (!isDirectTvNonCustom || isLocalChannel(channel.channel))
            ),
        [filteredChannels, isDirectTvNonCustom, readOnly, isLocalChannel]
    );

    const isAllSelected = useMemo(() => {
        if (selectableChannels.length === 0) return false;
        return selectableChannels.every((channel: any) => selectedChannelsSet.has(channel.channel));
    }, [selectableChannels, selectedChannelsSet]);

    const handleSelectAll = () => {
        // Don't allow select all if disabled
        if (readOnly || (isDirectTvNonCustom && selectableChannels.length === 0)) return;
        
        const channelsToToggle = isDirectTvNonCustom ? selectableChannels : filteredChannels;

        const updatedChannels = isAllSelected
            ? selectedChannels.filter(
                (id) => !channelsToToggle.some((ch: any) => ch.channel === id)
            )
            : [
                ...selectedChannels,
                ...channelsToToggle
                    .map((ch: any) => ch.channel)
                    .filter((id) => !selectedChannelsSet.has(id)),
            ];
        onChannelsChange(updatedChannels);
        setChannelArray(updatedChannels);
    };

    useEffect(() => {
        if (!isLoading) {
            onLoaded?.();
        }
    }, [isLoading, onLoaded]);

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.headerSection}>
                    <SkeletonCheckboxLoader /> {/* Placeholder for SearchInput */}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.headerSection}>
                <SearchInput onSearchChange={setSearchTerm} readOnly={readOnly} />
                <div className={styles.itemContainer}>
                    <input
                        type="checkbox"
                        className={styles.checkbox}
                        style={{
                            cursor: (readOnly || (isDirectTvNonCustom && selectableChannels.length === 0)) ? "not-allowed" : "pointer",
                            opacity: (readOnly || (isDirectTvNonCustom && selectableChannels.length === 0)) ? 0.6 : 1
                        }}
                        checked={isAllSelected}
                        disabled={readOnly || (isDirectTvNonCustom && selectableChannels.length === 0)}
                        onChange={handleSelectAll}
                        id={`select-all-${lineup.lineupId}`}
                        key={`channel-${lineup.lineupId}`}
                    />
                    <label
                        htmlFor={`select-all-${lineup.lineupId}`}
                        className={styles.label}
                    >
                        Select All
                    </label>
                </div>
            </div>

            {isError && (
                <p className={styles.errorMessage}>
                    Failed to load channels for {lineup.name}.
                </p>
            )}
            {filteredChannels.length === 0 && !isError && (
                <p className={styles.noChannels}>
                    No channels found for {searchTerm || lineup.name}.
                </p>
            )}

            <div className={styles.gridContainer} key={filteredChannels.length}>
                {filteredChannels.map((channel: any, index: number) => {
                    const channeLabel = `${channel.channel ? "(" + channel.channel + ")" : ""} ${channel?.callSign || ""}`;
                    const isSelectable = isChannelSelectable(channel.channel);
                    return (
                        <div
                            key={`${channel.stationId}-${index}`}
                            className={styles.itemContainer}
                        >
                            <input
                                type="checkbox"
                                checked={selectedChannels.includes(channel.channel) || false}
                                disabled={!isSelectable}
                                className={styles.checkbox}
                                style={{
                                    cursor: !isSelectable ? "not-allowed" : "pointer",
                                    opacity: !isSelectable ? 0.6 : 1
                                }}
                                onChange={() => handleChannelToggle(channel.channel)}
                                id={`channel-${channel.stationId}`}
                                key={`channel-${channel.stationId}`}
                            />
                            <label
                                htmlFor={`channel-${channel.stationId}`}
                                className={styles.label}
                            >
                                {channeLabel}
                            </label>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};